import { History } from 'history';
import Store from 'repatch';

import State from '../core/State';
import { createWithDefinition } from '../graphql/dynamic_schema';
import ProjectTypeWebApi from '../service/persistent/ProjectTypeWebApi';
import ProjectWebApi from '../service/persistent/ProjectWebApi';
import { decrement, increment } from '../thunk/fullScreenSpinner';
import { setIsOpenDesignInspirationScreen, setProjectType } from '../thunk/projectDesignInspiration';

class ProjectDesignInspirationUsecase {
  private readonly store: Store<State>;
  private readonly history: History;
  private readonly projectWebApi: ProjectWebApi;
  private readonly projectTypeWebApi: ProjectTypeWebApi;

  constructor({
    store,
    history,
    projectWebApi,
    projectTypeWebApi,
  }: {
    store: Store<State>;
    history: History;
    projectWebApi: ProjectWebApi;
    projectTypeWebApi: ProjectTypeWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.projectWebApi = projectWebApi;
    this.projectTypeWebApi = projectTypeWebApi;
  }

  public prepare = async ({ projectTypeKey }: { projectTypeKey: string }): Promise<void> => {
    this.store.dispatch(increment());

    const language = this.store.getState().language;
    const projectType = await this.projectTypeWebApi.getByKey(projectTypeKey, { language });
    if (projectType.designInspirationCategories) {
      this.store.dispatch(setProjectType(projectType));
    }
    this.store.dispatch(decrement());
  };

  public openDesignInspirationScreen = () => {
    this.store.dispatch(setIsOpenDesignInspirationScreen(true));
  };

  public closeDesignInspirationScreen = () => {
    this.store.dispatch(setIsOpenDesignInspirationScreen(false));
  };

  public toNext = async ({
    organizationId,
    designInspirationCaseId,
  }: {
    organizationId: string;
    designInspirationCaseId: string;
  }): Promise<void> => {
    this.store.dispatch(increment());
    const projectType = this.store.getState().projectEditing.projectType;
    if (projectType) {
      const creativeBriefValue = createWithDefinition(projectType.creativeBriefDefinition);

      if (projectType.assetCategoryDefinition == null) {
        throw new Error('projectType.assetCategoryDefinition must not null.');
      }
      const assetCategoryValue = createWithDefinition(projectType.assetCategoryDefinition);

      const projectId = await this.projectWebApi.createAsDraft({
        title: 'Untitled Project',
        matchingFormat: 'auto',
        projectType,
        creativeBriefValue,
        assetCategoryValue,
        organizationId,
        designInspirationCaseId,
      });
      this.history.replace(`/projects/${projectId}/edit`);
      this.store.dispatch(decrement());
    }
  };
}

export default ProjectDesignInspirationUsecase;
